import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  buildCartMutationToken,
  resolveCartLine,
  resolveEnhancedStepperErrorState,
  shouldSkipQueuedStepperResponse,
} from './cart-items-logic.js';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  /** @type {Map<string, { quantity: number, fallbackLine: number, lineItemKey?: string, previousQuantity?: number }>} */
  #pendingEnhancedStepperQuantities = new Map();
  /** @type {number} */
  #latestCartMutationRequestId = 0;
  /** @type {boolean} */
  #pendingPostMutationRefresh = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#handleEnhancedQtyStepperClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#handleEnhancedQtyStepperClick);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const lineItemKey = lineItemRow?.dataset.key;
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);
    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles enhanced drawer quantity stepper clicks.
   * Keeps enhanced cart quantity updates on the same module path as other cart quantity changes.
   * @param {MouseEvent} event
   */
  #handleEnhancedQtyStepperClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-qty-stepper] [data-action]');
    if (!(button instanceof HTMLElement)) return;
    if (!this.contains(button)) return;

    event.preventDefault();

    const action = button.dataset.action;
    const line = Number(button.dataset.lineIndex || 0);
    if (!line || (action !== 'increase' && action !== 'decrease')) return;

    const stepper = button.closest('[data-qty-stepper]');
    const valueEl = stepper?.querySelector('[data-qty-value]');
    const row = button.closest('tr[data-quantity]');
    const lineItemKey = row?.dataset.key;
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);

    const rowQuantity = Number(row?.dataset.quantity || 0);
    const currentQuantity = rowQuantity > 0 ? rowQuantity : Number(valueEl?.textContent || 1);
    if (!Number.isFinite(currentQuantity) || currentQuantity <= 0) return;

    const previousQuantity = currentQuantity;
    let nextQuantity = action === 'increase' ? currentQuantity + 1 : currentQuantity - 1;
    if (nextQuantity > 99) return;
    if (nextQuantity < 0) nextQuantity = 0;
    if (nextQuantity === currentQuantity) return;

    if (valueEl && nextQuantity > 0) {
      valueEl.textContent = String(nextQuantity);
    }
    if (row) {
      row.dataset.quantity = String(nextQuantity);
    }

    if (this.classList.contains('cart-items-disabled')) {
      this.#pendingEnhancedStepperQuantities.set(requestToken, {
        quantity: nextQuantity,
        fallbackLine: line,
        lineItemKey: lineItemKey || undefined,
        previousQuantity,
      });
      return;
    }

    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    if (nextQuantity === 0) {
      this.onLineItemRemove(line);
      return;
    }

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity: nextQuantity,
      previousQuantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Applies the most recent queued enhanced stepper change after current cart mutation settles.
   */
  #flushPendingEnhancedStepper = () => {
    if (this.classList.contains('cart-items-disabled')) return;
    if (!this.#pendingEnhancedStepperQuantities.size) return;

    while (this.#pendingEnhancedStepperQuantities.size && !this.classList.contains('cart-items-disabled')) {
      const [requestToken, pending] = this.#pendingEnhancedStepperQuantities.entries().next().value || [];
      if (!requestToken || !pending || typeof pending.quantity !== 'number') return;

      this.#pendingEnhancedStepperQuantities.delete(requestToken);

      const resolvedLine = this.#resolveCartLine(pending.fallbackLine, pending.lineItemKey);
      if (!resolvedLine) continue;

      const lineItemRow = this.refs.cartItemRows[resolvedLine - 1];
      if (!lineItemRow) continue;

      lineItemRow.dataset.quantity = String(pending.quantity);

      if (pending.quantity === 0) {
        this.onLineItemRemove(resolvedLine);
        return;
      }

      this.updateQuantity({
        line: resolvedLine,
        lineItemKey: pending.lineItemKey,
        requestToken,
        quantity: pending.quantity,
        previousQuantity: pending.previousQuantity,
        action: 'change',
      });

      const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
      return;
    }
  };

  /**
   * Builds a stable mutation token for a cart line update.
   * @param {number} line
   * @param {string | undefined} lineItemKey
   * @returns {string}
   */
  #buildCartMutationToken = (line, lineItemKey) => {
    return buildCartMutationToken(line, lineItemKey);
  };

  /**
   * Resolves the current cart line index from a stable line-item key.
   * @param {number} fallbackLine
   * @param {string | undefined} lineItemKey
   * @returns {number}
   */
  #resolveCartLine = (fallbackLine, lineItemKey) => {
    return resolveCartLine(this.refs.cartItemRows, fallbackLine, lineItemKey);
  };

  /**
   * Schedules a post-mutation section refresh when external cart updates arrive
   * during an in-flight local cart mutation.
   */
  #queuePostMutationRefresh = () => {
    this.#pendingPostMutationRefresh = true;
  };

  /**
   * Runs a deferred refresh if one was queued while cart mutations were in flight.
   */
  #maybeRunPostMutationRefresh = () => {
    if (!this.#pendingPostMutationRefresh) return;
    if (this.classList.contains('cart-items-disabled')) return;

    this.#pendingPostMutationRefresh = false;
    sectionRenderer.renderSection(this.sectionId, { cache: false });
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const lineItemRowToRemove = this.refs.cartItemRows[line - 1];
    const lineItemKey = lineItemRowToRemove?.dataset.key;
    const requestToken = this.#buildCartMutationToken(line, lineItemKey);
    this.#pendingEnhancedStepperQuantities.delete(requestToken);

    this.updateQuantity({
      line,
      lineItemKey,
      requestToken,
      quantity: 0,
      action: 'clear',
    });

    if (!lineItemRowToRemove) return;

    const rowsToRemove = [
      lineItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === lineItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {string} [config.lineItemKey] - Stable line-item key for race-safe updates.
   * @param {string} [config.requestToken] - Stable mutation token.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const mutationRequestId = ++this.#latestCartMutationRequestId;

    const { line, lineItemKey, quantity, previousQuantity } = config;
    const requestToken = config.requestToken || this.#buildCartMutationToken(line, lineItemKey);
    const { cartTotal } = this.refs;

    if (!line || line < 1) return;
    
    this.#disableCartItems();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {{ line: number, quantity: number, sections: string, sections_url: string }} */
    const payload = {
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        if (mutationRequestId !== this.#latestCartMutationRequestId) {
          return;
        }

        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText, { lineItemKey, requestToken, previousQuantity });
          return;
        }

        // If a newer enhanced-stepper quantity is already queued for this line,
        // skip rendering this stale intermediate response to avoid visual jump-back.
        const queuedQuantityForLine = this.#pendingEnhancedStepperQuantities.get(requestToken);
        const isStaleQueuedStepperResponse = shouldSkipQueuedStepperResponse(queuedQuantityForLine, quantity);

        if (isStaleQueuedStepperResponse) {
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], this.isDrawer ? 'hydration' : 'full');

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        if (mutationRequestId !== this.#latestCartMutationRequestId) {
          cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
          return;
        }

        this.#enableCartItems();
        this.#flushPendingEnhancedStepper();
        this.#maybeRunPostMutationRefresh();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {{ lineItemKey?: string, requestToken?: string, previousQuantity?: number }} [options]
   */
  #handleCartError = (line, parsedResponseText, options = {}) => {
    if (options.requestToken) {
      this.#pendingEnhancedStepperQuantities.delete(options.requestToken);
    }

    const {
      resolvedLine,
      row,
      quantity: resetQuantity,
    } = resolveEnhancedStepperErrorState(this.refs.cartItemRows, line, options.lineItemKey, options.previousQuantity);

    const quantitySelectors = this.refs.quantitySelectors || [];
    const quantitySelector = quantitySelectors[resolvedLine - 1] || quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const enhancedValue = row instanceof HTMLElement ? row.querySelector('[data-qty-value]') : null;
    if (enhancedValue) {
      enhancedValue.textContent = String(resetQuantity);
    }
    if (row instanceof HTMLElement) {
      row.dataset.quantity = String(resetQuantity);
    }

    const cartItemError = this.refs[`cartItemError-${resolvedLine}`] || this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer =
      this.refs[`cartItemErrorContainer-${resolvedLine}`] || this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) return;
    if (!(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (this.classList.contains('cart-items-disabled')) {
        this.#queuePostMutationRefresh();
        return;
      }

      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    if (this.classList.contains('cart-items-disabled')) {
      this.#queuePostMutationRefresh();
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
